import React, { useState } from 'react';
import {
  Box,
  FormHelperText,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';

import colors from '../../../themes/main-colors';
import TextField from '../../text-field';
import { SearchServiceAddress } from '../../../__generated__/pge-types';
import ServiceAddressLookup from '../../service-address-lookup/ServiceAddressLookup';
import { useTranslation } from '../../../hooks/useTranslation';
import { SelectDropdown } from '../Form/SelectDropdown';
import { FormState } from '../../../hooks/useFormState.types';
import { IQBDModel } from '../IQBDForm.rules';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import useContentMgmt from '../../../hooks/useContentMgmt';
import AccountTextField from '../../account-text-field';

interface CustomerDetailsProps {
  form: FormState<IQBDModel>;
  setPremiseId: any;
  setIsmultiPremiseId: any;
}

const useStyles = makeStyles((theme: Theme) => ({
  applyingForBox: {
    border: `1px solid ${colors.lightGray}`,
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '48px 0',
    width: '333px',
    textAlign: 'center',
    fontSize: '18px',
    cursor: 'pointer',
    fontFamily: 'Untitled-Sans',
    transition: 'all 0.2s',
  },
  selectedApplyingForBox: {
    background: colors.riverBlue,
    boxShadow: '0px 3px 6px #0000004D',
    fontWeight: 500,
  },
  miniApplyingForBox: {
    padding: '12px',
  },
  formSection: {
    background: `${colors.white} 0% 0% no-repeat padding-box`,
    borderRadius: '5px',
    padding: '30px 20px',
    margin: '10px 0',
  },
  sectionHeading: {
    fontFamily: 'Forma-DJR-Display',
    fontWeight: 'bold',
    fontSize: '24px',
    lineHeight: '28px',
    letterSpacing: '0.48px',
    color: colors.lightCyan,
    marginBottom: '15px',
  },
  sectionInfo: {
    fontFamily: 'Untitled-Sans',
    fontSize: '16px',
    lineHeight: '20px',
    color: colors.lightCyan,
    display: 'block',
    margin: '5px 0',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  highlighted: {
    textDecoration: 'underline',
    fontWeight: 'bold',
    color: colors.sparkBlue,
    cursor: 'pointer',
    fontSize: 'inherit',
    lineHeight: 'inherit',
  },
  button: {
    borderRadius: '5px',
    padding: '13px 57px',
    fontWeight: 'bold',
    fontSize: '18px',
    fontFamily: 'Untitled-Sans',
    display: 'block',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  listItem: {
    display: 'list-item',
    padding: 0,
    fontSize: '16px',
    lineHeight: '20px',
    fontFamily: 'Untitled-Sans',
    margin: '8px 0',
  },
  textfield: {
    margin: theme.spacing(0.25, 0, 0, 0),
    '& label': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '90%',
      fontSize: '16px',
      transform: 'translate(14px, 16px) scale(1)',
      '&.MuiInputLabel-shrink': {
        overflow: 'unset',
      },
    },
    '& input': {
      height: '48px',
      fontSize: '16px',
      padding: '0 14px',
    },
  },
}));

const IQBD_FORM_MICROGROUP_COPY_ID = '6wxqSYOBj1ybyYgyRPeQOA';

export const CustomerDetails: React.FC<CustomerDetailsProps> = props => {
  const { form, setPremiseId, setIsmultiPremiseId } = props;
  const { content } = useContentMgmt(IQBD_FORM_MICROGROUP_COPY_ID);
  const classes = useStyles();
  const { t, richT } = useTranslation();

  const [inputValue, setInputValue] = useState<string>('');
  const [isAddressHelpDisplayed, setIsAddressHelpDisplayed] = useState<boolean>(
    false,
  );

  const handleAddressSelected = (address: SearchServiceAddress) => {
    setPremiseId(address?.premiseIds?.[0]!);
    if (address?.premiseIds && address?.premiseIds?.length! > 1) {
      setIsmultiPremiseId(true);
    }
    let completeAddress: string = '';
    if (address && address.addressLine1 && address.city && address.postal) {
      completeAddress = `${address.addressLine1.trim()}, ${address.city.trim()}, ${address.postal.trim()}`;
    }
    void form.setValue('address', completeAddress);
    setInputValue(completeAddress);
  };

  return (
    <>
      <Typography css={{ marginBottom: '16px' }}>
        <strong>
          {content.get('CONTACT_INFO_OF_THE_PERSON_SUBMITTING_THE_APP_FOR')}
        </strong>
      </Typography>
      <Grid
        container
        css={{
          backgroundColor: colors.white,
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          borderRadius: '5px',
        }}
      >
        <Grid item xs={12} md={6}>
          <Typography className={classes.sectionInfo}>
            {content.get('ACCOUNT_NUMBER')}
          </Typography>
          <AccountTextField
            className={classes.textfield}
            name={content.get('ACCOUNT_NUMBER')}
            label={content.get('ACCOUNT_NUMBER')}
            value={form.values.accountNumber}
            onChange={(e: any) => {
              void form.setValue('accountNumber', e.target.value);
            }}
          />
          {form.errors.accountNumber && (
            <FormHelperText error={true}>
              {form.errors.accountNumber}
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12}>
          <Grid item xs={12}>
            <Typography className={classes.sectionInfo}>
              {content.get('SERVICE_ADDRESS')}
            </Typography>
            <Typography
              className={classes.sectionInfo}
              css={{
                marginTop: '8px',
                fontSize: ' 0.8rem',
              }}
            >
              {t('FOR_BEST_RESULTS_SELECT_AN_OPTION')}
            </Typography>
            <Box css={{ marginBottom: '8px' }}>
              <a
                href="#"
                onClick={(e: any) => {
                  e.preventDefault();
                  setIsAddressHelpDisplayed(!isAddressHelpDisplayed);
                }}
                style={{
                  cursor: 'pointer',
                  textDecoration: 'none',
                  display: 'inline',
                  fontSize: ' 0.8rem',
                }}
              >
                <Box display="flex">
                  <span
                    style={{
                      color: colors.sparkBlue,
                      fontSize: ' 0.8rem',
                    }}
                  >
                    {t('MORE_ADDRESS_HELP')}
                  </span>
                  {isAddressHelpDisplayed ? <ExpandLess /> : <ExpandMore />}
                </Box>
              </a>
              {isAddressHelpDisplayed && (
                <ul style={{ margin: '0px' }}>
                  <li>{richT('CHECK_TO_SEE_IF_THE_ADDRESS')}</li>
                  <li>
                    {richT('CALL_US_AT', {
                      CUSTOMER_SERVICE_NUMBER: t('CALL_FOR_ASSISTANCE_NUMBER'),
                    })}
                  </li>
                </ul>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={12}>
            <ServiceAddressLookup
              onAddressChange={handleAddressSelected}
              inputFieldProps={{
                variant: 'outlined',
                label: t('STREET_ADDRESS'),
                error: false,
                value: inputValue,
                onChange(e) {
                  setInputValue(e.target.value);
                },
              }}
              autocompleteProps={{
                freeSolo: false,
                inputValue,
              }}
            />
            {form.errors.address && (
              <FormHelperText error={true}>
                {form.errors.address}
              </FormHelperText>
            )}
          </Grid>
        </Grid>
        <Grid container xs={12}>
          <Typography className={classes.sectionInfo}>
            {content.get('CUSTOMER_NAME')}
          </Typography>
          <Grid container item spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                className={classes.textfield}
                name={content.get('FIRST_NAME')}
                label={content.get('FIRST_NAME')}
                value={form.values.customerFirstName}
                onChange={(e: any) => {
                  void form.setValue('customerFirstName', e.target.value);
                }}
              />
              {form.errors.customerFirstName && (
                <FormHelperText error={true}>
                  {form.errors.customerFirstName}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                className={classes.textfield}
                name={content.get('LAST_NAME')}
                label={content.get('LAST_NAME')}
                value={form.values.customerLastName}
                onChange={(e: any) => {
                  void form.setValue('customerLastName', e.target.value);
                }}
              />
              {form.errors.customerLastName && (
                <FormHelperText error={true}>
                  {form.errors.customerLastName}
                </FormHelperText>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography className={classes.sectionInfo}>
            {content.get('CUSTOMER_EMAIL')}
          </Typography>
          <TextField
            className={classes.textfield}
            name={'Customer email'}
            label={'Customer email'}
            value={form.values.customerEmail}
            onChange={(e: any) => {
              void form.setValue('customerEmail', e.target.value);
            }}
          />
          {form.errors.customerEmail && (
            <FormHelperText error={true}>
              {form.errors.customerEmail}
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectDropdown
            title={content.get('RELATION_TO_CUSTOMER') || ''}
            selectedValue={
              form.values.customerRelType
                ? form.values.customerRelType.toString()
                : ''
            }
            placeholder={content.get('PLEASE_SELECT')}
            options={[
              content.get('AGENCY_ADVOCATE'),
              content.get('FAMILY_MEMBER'),
              content.get('RELATIONSHIP_SPOUSE'),
              content.get('RELATIONSHIP_FRIEND'),
              content.get('OTHER'),
            ]}
            onChange={value => {
              void form.setValue('customerRelType', value);
            }}
          />
          {form.errors.customerRelType && (
            <FormHelperText error={true}>
              {form.errors.customerRelType}
            </FormHelperText>
          )}
        </Grid>
      </Grid>
    </>
  );
};
